/**
 * Event colour tokens for AdminCalendarPage (T2.1.2).
 *
 * Status drives the primary fill (background + text). Priority drives
 * the left/edge border. We additionally attach className markers on
 * every event so future CSS overrides can target status/priority
 * independently of inline colour props — `borderColor` alone has been
 * unreliable in some FullCalendar views (list / dayGrid month) where
 * the border is collapsed by the layout.
 *
 * Palette: the same `#xxxxxx` hex values used by the T1 list-page pills
 * (StatusPill / PriorityPill) — picking different values here would
 * give us two visually-different "status: done" indicators on the same
 * screen, which we don't want.
 */

/** status → { backgroundColor, textColor, classNames[] } */
export const TASK_STATUS_COLORS = {
  todo: {
    backgroundColor: "#1e2238",
    textColor: "#c5cbe0",
    classNames: ["fc-task-status-todo"],
  },
  in_progress: {
    backgroundColor: "#3a2f1f",
    textColor: "#f0b86e",
    classNames: ["fc-task-status-in-progress"],
  },
  done: {
    backgroundColor: "#13261a",
    textColor: "#6ee7a8",
    classNames: ["fc-task-status-done"],
  },
  cancelled: {
    backgroundColor: "#13161f",
    textColor: "#4a5070",
    classNames: ["fc-task-status-cancelled", "fc-task-opacity-50"],
  },
};

/** priority → { borderColor, classNames[] } */
export const TASK_PRIORITY_COLORS = {
  low: {
    borderColor: "#1e2130",
    classNames: ["fc-task-priority-low"],
  },
  medium: {
    borderColor: "#1e2130",
    classNames: ["fc-task-priority-medium"],
  },
  high: {
    borderColor: "#f0b86e",
    classNames: ["fc-task-priority-high"],
  },
  urgent: {
    borderColor: "#ff6b6b",
    classNames: ["fc-task-priority-urgent"],
  },
};

/**
 * Merge status + priority styling into the props a FullCalendar Event
 * object accepts directly. Returns:
 *   { backgroundColor, borderColor, textColor, classNames[] }
 *
 * Unknown status/priority values fall back to "todo" / "medium".
 */
export function getEventColors(task) {
  const status =
    TASK_STATUS_COLORS[task?.status] || TASK_STATUS_COLORS.todo;
  const priority =
    TASK_PRIORITY_COLORS[task?.priority] || TASK_PRIORITY_COLORS.medium;
  return {
    backgroundColor: status.backgroundColor,
    borderColor: priority.borderColor,
    textColor: status.textColor,
    classNames: [...status.classNames, ...priority.classNames],
  };
}


// ─── Kalender P2a / CAL-WEEK1: assignee color-coding ──────────────────

/**
 * Per-person event colours.
 *
 * ## What changed in CAL-WEEK1, and why
 *
 * The colour used to be the person's INDEX in the users array. The
 * array arrives email-sorted from the server
 * (``routes_admin_users.py:152``), so a new colleague whose email
 * sorted early re-coloured everyone after them — and a person's colour
 * is supposed to be the thing you recognise them by. It is now derived
 * from their id.
 *
 * ## Why not a plain hash
 *
 * Because a plain hash does not give DISTINCT colours, and distinctness
 * is the whole point. Hashing N people into P slots leaves them all
 * different with probability P!/((P−N)!·P^N) — for six slots that is
 * 56% at three people and 9% at five. Two colleagues sharing a colour
 * most of the time defeats the feature far more thoroughly than an
 * occasional reshuffle does.
 *
 * So: hash for the PREFERENCE, then resolve collisions deterministically
 * by probing forward. The result is
 *
 * * deterministic — same roster, same colours, every render and reload;
 * * independent of wire order — sorting the input changes nothing;
 * * distinct for everyone present, up to the palette size;
 * * and stable for a person across roster changes UNLESS somebody new
 *   lands on their exact slot, which is the only case where anyone
 *   moves at all. Under index assignment, every insertion moved half
 *   the team.
 *
 * True permanence across roster changes needs a persisted per-user
 * colour, which is a database column and its own slice. This is the
 * best available without one.
 *
 * ## Why colour is never the only signal
 *
 * Twelve colours cannot separate twenty people, and roughly 1 in 12 men
 * has a colour vision deficiency. Every surface that uses these also
 * shows the name or the initials — the filter chips
 * (``CalendarFilters.jsx``), the event pill, the sidebar row. Colour is
 * the fast channel, not the only one.
 */

/**
 * Twelve pastel-but-saturated hues, evenly spread around the wheel and
 * ordered so neighbouring palette entries are far apart — two people
 * who land in adjacent slots should not look similar.
 *
 * Every entry is dark enough to carry white text: ``CalendarFilters``
 * hardcodes ``text-white`` on the avatar chip, so a light entry would
 * be unreadable there. On the event pill these are fed through
 * ``color-mix()`` in ``index.css``, which derives both the light and
 * the dark rendering from this one value.
 *
 * Widened from six in CAL-WEEK1: with six, an organisation of seven
 * already had two people sharing a colour by construction.
 */
export const ASSIGNEE_PALETTE = [
  "#378ADD", // blue
  "#1D9E75", // green
  "#7F77DD", // violet
  "#BA7517", // amber-brown
  "#D4537E", // pink
  "#D85A30", // orange
  "#2E9BA4", // teal
  "#8E6CC4", // purple
  "#4F8A2E", // olive
  "#C2426A", // raspberry
  "#3F72C9", // indigo
  "#A6701F", // bronze
];

export const UNASSIGNED_COLOR = "#64748b";

/**
 * FNV-1a over the id string.
 *
 * Chosen because it is tiny, has no dependencies, and spreads short
 * similar strings — sequential UUIDs, incrementing ids — far better
 * than the classic ``h*31+c`` loop does. The exact function does not
 * matter to correctness, only that it is pure and stable; it is
 * exported so a test can pin that stability directly.
 */
export function hashPersonId(id) {
  const s = String(id);
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    // Multiply by the 32-bit FNV prime without overflowing into float
    // territory; >>> 0 keeps it unsigned.
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/**
 * Build ``{userId: colour}`` for one roster.
 *
 * Deterministic in the SET of ids, not in their order: the input is
 * sorted before collision resolution runs, so the same people always
 * produce the same map however the API happened to order them.
 */
export function buildAssigneeColorMap(users) {
  const list = (Array.isArray(users) ? users : []).filter(
    (u) => u && u.id != null
  );

  // Sorted so that WHICH of two colliding people keeps the preferred
  // slot is a property of their ids, not of the response order. Without
  // this the map would still be collision-free but would flip between
  // two arrangements as the wire order changed.
  const ids = list
    .map((u) => String(u.id))
    .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));

  const map = {};
  const taken = new Set();
  const size = ASSIGNEE_PALETTE.length;

  for (const id of ids) {
    if (map[id] !== undefined) continue; // duplicate id in the roster
    const preferred = hashPersonId(id) % size;
    let slot = preferred;
    // Linear probe. Bounded by the palette size, so it terminates even
    // when the roster is larger than the palette — past that point
    // colours necessarily repeat and the name carries the identity.
    for (let step = 0; step < size; step += 1) {
      const candidate = (preferred + step) % size;
      if (!taken.has(candidate)) {
        slot = candidate;
        break;
      }
    }
    taken.add(slot);
    map[id] = ASSIGNEE_PALETTE[slot];
  }
  return map;
}

/**
 * Resolve a user id to its colour, or the neutral grey.
 *
 * Unchanged contract: unknown id, null id, or absent map all give the
 * unassigned colour rather than throwing. Several callers pass a map
 * that may not have loaded yet.
 */
export function colorForAssignee(userId, colorMap) {
  if (userId == null) return UNASSIGNED_COLOR;
  const color = colorMap ? colorMap[String(userId)] : undefined;
  return color || UNASSIGNED_COLOR;
}

/**
 * Initials for a person, for the places where a name does not fit.
 *
 * First + last initial, or the first two letters of a single-token name,
 * falling back to the first letter of the email and finally to ``?``.
 *
 * Lives here rather than in ``CalendarFilters`` — where it started —
 * because CAL-WEEK1 needs the same two letters in three places: the
 * filter chip, the event pill in a narrow column, and the sidebar row.
 * Three copies of "how do we abbreviate a person" would drift, and a
 * person abbreviated two different ways on one screen reads as two
 * people.
 *
 * Accepts either a user object or a bare name string, because the event
 * pill only ever has the name — the calendar event carries
 * ``assignedToFullName``, not the roster row.
 */
export function initialsFor(person) {
  const isObject = person && typeof person === "object";
  const raw = String((isObject ? person.full_name : person) || "").trim();

  // A bare string may be an EMAIL rather than a name — the pill falls
  // back to the address when the roster has no full name for someone.
  // Running "s.ieri@example.test" through the name branch yields "S." ,
  // punctuation the sidebar would never show for the same person. Treat
  // it as the email it is.
  if (!isObject && raw.includes("@")) {
    return raw[0].toUpperCase();
  }

  const name = raw;
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
  }
  const email = String((isObject ? person.email : "") || "").trim();
  return email ? email[0].toUpperCase() : "?";
}
